"use client"

import { useState, useRef } from "react"
import Link from "next/link"
import Image from "next/image"
import { Loader2, CheckCircle, ArrowLeft } from "lucide-react"
import MobileNav from "@/components/MobileNav"
import { Turnstile } from "@marsidev/react-turnstile"

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" })
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle")
  const [errorMsg, setErrorMsg] = useState("")
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const loadedAt = useRef(Date.now())

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus("loading")
    setErrorMsg("")

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, _hp: "", _t: loadedAt.current, turnstileToken }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Failed to send")
      setStatus("success")
    } catch (err) {
      setStatus("error")
      setErrorMsg(err instanceof Error ? err.message : "Something went wrong.")
    }
  }

  const inputCls = "w-full px-4 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1A2744] focus:border-transparent transition"

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#F7F4EE" }}>
      {/* Nav */}
      <header className="sticky top-0 z-50 flex items-center justify-between px-4 sm:px-8 h-16 border-b shadow-sm" style={{ background: "#F7F4EE", borderColor: "#E8E2D9" }}>
        <Link href="/" className="flex items-center flex-shrink-0">
          <Image src="/logo.png" alt="Blue Collar Social" width={140} height={37} priority />
        </Link>
        <nav className="hidden md:flex gap-6 text-sm font-medium" style={{ color: "#4A5568" }}>
          <Link href="/jobs" className="hover:text-[#1A2744] transition-colors">Browse Jobs</Link>
          <Link href="/search" className="hover:text-[#1A2744] transition-colors">Find Tradespeople</Link>
          <Link href="/blog" className="hover:text-[#1A2744] transition-colors">Insights</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link href="/login" className="hidden sm:block text-sm font-medium text-gray-600 hover:text-[#1A2744] px-3 py-1.5 transition-colors">
            Log in
          </Link>
          <Link href="/register" className="text-xs sm:text-sm font-semibold text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-md transition-colors" style={{ background: "#1A2744" }}>
            Get Started
          </Link>
          <MobileNav />
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-start py-12 px-4">
        <div className="w-full max-w-xl">
          <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-8 transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>

          {status === "success" ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-10 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-gray-900 mb-2">Message sent!</h1>
              <p className="text-gray-500 text-sm mb-6">Thanks for reaching out. We'll get back to you shortly.</p>
              <Link href="/" className="text-sm font-medium text-[#1A2744] hover:underline">
                ← Back to Blue Collar Social
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-8 py-7 border-b border-gray-100" style={{ background: "#1A2744" }}>
                <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Georgia, serif" }}>
                  Get in touch
                </h1>
                <p className="text-sm mt-1.5" style={{ color: "rgba(255,255,255,0.55)" }}>
                  Questions, partnerships, or feedback — we'd love to hear from you.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="px-8 py-7 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      name="name" type="text" required
                      value={form.name} onChange={handleChange}
                      placeholder="Your name"
                      className={inputCls}
                      disabled={status === "loading"}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Email <span className="text-red-400">*</span>
                    </label>
                    <input
                      name="email" type="email" required
                      value={form.email} onChange={handleChange}
                      placeholder="you@example.com"
                      className={inputCls}
                      disabled={status === "loading"}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Subject
                  </label>
                  <select
                    name="subject"
                    value={form.subject} onChange={handleChange}
                    className={inputCls}
                    disabled={status === "loading"}
                  >
                    <option value="">Select a topic…</option>
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Partnership">Partnership Opportunity</option>
                    <option value="Press / Media">Press / Media</option>
                    <option value="Technical Issue">Technical Issue</option>
                    <option value="Account Help">Account Help</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    Message <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message" required rows={5}
                    value={form.message} onChange={handleChange}
                    placeholder="Tell us what's on your mind…"
                    className={inputCls}
                    disabled={status === "loading"}
                    style={{ resize: "vertical" }}
                  />
                </div>

                {status === "error" && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-4 py-3 text-sm">
                    {errorMsg}
                  </div>
                )}

                {/* Honeypot */}
                <input type="text" name="_hp" style={{ display: "none" }} tabIndex={-1} autoComplete="off" aria-hidden="true" />

                {process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
                  <Turnstile
                    siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    options={{ theme: "light" }}
                  />
                )}

                <button
                  type="submit"
                  disabled={status === "loading" || (!!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && !turnstileToken)}
                  className="w-full py-3 rounded-md text-sm font-semibold text-white transition-colors disabled:opacity-60"
                  style={{ background: "#C8922A" }}
                >
                  {status === "loading" ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </span>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </form>
            </div>
          )}
        </div>
      </main>

      <footer className="text-center py-6 text-xs font-mono" style={{ color: "#8C8780" }}>
        © {new Date().getFullYear()} <strong style={{ color: "#C8922A" }}>Blue Collar Social</strong>
      </footer>
    </div>
  )
}
